import { type components, isApiError } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/lib/api'
import { formatErrorMessage } from '@/lib/error-message'

/**
 * The cheque API (WP09). A cheque is a promise of money tracked on its own:
 * the server says which steps it may take next (`next_statuses`) and posts
 * to the ledger; nothing here derives a rule. Keys live under
 * ['companies', company, 'accounting', 'cheques'].
 */

export type Cheque = components['schemas']['ChequeResource']
export type ChequeRequest = components['schemas']['StoreChequeRequest']
export type ChequeStep = 'deposit' | 'clear' | 'bounce' | 'cancel' | 'endorse' | 'reschedule'
export type ChequeStepBody = components['schemas']['ChequeStepRequest']

const STEP_PATHS = {
  deposit: '/accounting/cheques/{cheque}/deposit',
  clear: '/accounting/cheques/{cheque}/clear',
  bounce: '/accounting/cheques/{cheque}/bounce',
  cancel: '/accounting/cheques/{cheque}/cancel',
  endorse: '/accounting/cheques/{cheque}/endorse',
  reschedule: '/accounting/cheques/{cheque}/reschedule',
} as const

export const CHEQUE_DIRECTIONS = ['received', 'issued'] as const
export type ChequeDirection = (typeof CHEQUE_DIRECTIONS)[number]
export const CHEQUE_STATUSES = [
  'outstanding',
  'held',
  'deposited',
  'cleared',
  'bounced',
  'cancelled',
  'endorsed',
] as const
export type ChequeStatusFilter = (typeof CHEQUE_STATUSES)[number]

export type ChequeFilters = {
  direction?: ChequeDirection
  status?: ChequeStatusFilter
  q?: string
  page?: number
  perPage?: number
}

const alertSchema = z.object({
  data: z.object({ days: z.number(), due_soon: z.number(), overdue: z.number() }),
})

export const chequeKeys = {
  all: (companyId: string | null) => ['companies', companyId, 'accounting', 'cheques'] as const,
  list: (companyId: string | null, filters: ChequeFilters) =>
    [...chequeKeys.all(companyId), 'list', filters] as const,
  detail: (companyId: string | null, id: string) =>
    [...chequeKeys.all(companyId), 'detail', id] as const,
  alert: (companyId: string | null) => [...chequeKeys.all(companyId), 'alert'] as const,
}

export function useCheques(companyId: string | null, filters: ChequeFilters) {
  return useQuery({
    queryKey: chequeKeys.list(companyId, filters),
    enabled: Boolean(companyId),
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await api.GET('/accounting/cheques', {
        headers: { 'X-Company-Id': companyId ?? '' },
        params: {
          query: {
            direction: filters.direction,
            status: filters.status,
            search: filters.q,
            page: filters.page,
            per_page: filters.perPage ?? 25,
          },
        },
      })
      if (!response.data) throw new Error('GET /accounting/cheques returned no body')
      return response.data
    },
  })
}

export function useCheque(companyId: string | null, id: string) {
  return useQuery({
    queryKey: chequeKeys.detail(companyId, id),
    enabled: Boolean(companyId),
    queryFn: async (): Promise<Cheque> => {
      const response = await api.GET('/accounting/cheques/{cheque}', {
        headers: { 'X-Company-Id': companyId ?? '' },
        params: { path: { cheque: id } },
      })
      if (!response.data) throw new Error('GET /accounting/cheques/{cheque} returned no body')
      return response.data.data
    },
  })
}

/** Outstanding cheques reaching their cashing date within a week, and those past it. */
export function useChequeAlert(companyId: string | null) {
  return useQuery({
    queryKey: chequeKeys.alert(companyId),
    enabled: Boolean(companyId),
    queryFn: async () => {
      const response = await api.GET('/accounting/cheques/alert', {
        headers: { 'X-Company-Id': companyId ?? '' },
        params: { query: { days: 7 } },
      })
      return alertSchema.parse(response.data).data
    },
  })
}

export function useBankAccountOptions(companyId: string | null) {
  return useQuery({
    queryKey: ['companies', companyId, 'accounting', 'bank-accounts'],
    enabled: Boolean(companyId),
    queryFn: async () => {
      const response = await api.GET('/accounting/bank-accounts', {
        headers: { 'X-Company-Id': companyId ?? '' },
      })
      if (!response.data) throw new Error('GET /accounting/bank-accounts returned no body')
      return response.data.data.filter(
        (account) => account.is_active && account.account_type === 'bank',
      )
    },
  })
}

/** The first thing the server said was wrong, in the reader's language. */
export function chequeErrorMessage(error: unknown, fallback: string): string {
  const first = isApiError(error) ? Object.values(error.errors ?? {})[0]?.[0] : undefined
  return first ?? formatErrorMessage(error, fallback)
}

export function useRecordCheque(companyId: string) {
  const queryClient = useQueryClient()

  return useMutation<Cheque, Error, ChequeRequest>({
    mutationFn: async (body) => {
      const response = await api.POST('/accounting/cheques', {
        headers: { 'X-Company-Id': companyId },
        body,
      })
      if (!response.data) throw new Error('POST /accounting/cheques returned no body')
      return response.data.data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chequeKeys.all(companyId) }),
  })
}

export function useChequeStep(companyId: string, id: string) {
  const queryClient = useQueryClient()

  return useMutation<Cheque, Error, { step: ChequeStep; body: ChequeStepBody }>({
    mutationFn: async ({ step, body }) => {
      const response = await api.POST(STEP_PATHS[step], {
        headers: { 'X-Company-Id': companyId },
        params: { path: { cheque: id } },
        body,
      })
      if (!response.data) throw new Error('POST cheque step returned no body')
      return response.data.data
    },
    // A step moves money or a debt: payments, invoices, bills and the bank change with it.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'accounting'] }),
  })
}
